/**
 * RapidTriage — keyboard-first, full-screen media review.
 * A approve · R reject · S skip · ←/→ move · Space expand · Enter details ·
 * Esc exit. Optimistic decisions, instant transitions, session tally.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, X, SkipForward, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { editorialDecide } from "../../lib/api";
import { AuthImg } from "../media/AuthImg";

const confColor = (n) => (n >= 70 ? "#3FB950" : n >= 40 ? "#E0A526" : "#D92D20");

const Key = ({ k, label }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#757065]">
    <kbd className="border border-[#3A3630] bg-[#151310] px-1.5 py-0.5 text-[#E4DFD3] text-[10px]">{k}</kbd>
    {label}
  </span>
);

export const RapidTriage = ({ queue, token, userToken, onClose, onDecided }) => {
  const [idx, setIdx] = useState(0);
  const [decided, setDecided] = useState({});   // id -> action
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(false);
  const items = queue;
  const item = items[idx];
  const tally = useMemo(() => {
    const t = { approve: 0, reject: 0, skip: 0 };
    Object.values(decided).forEach((a) => { t[a] = (t[a] || 0) + 1; });
    return t;
  }, [decided]);

  const move = useCallback((dir) => {
    setExpanded(false); setDetails(false);
    setIdx((i) => Math.min(items.length - 1, Math.max(0, i + dir)));
  }, [items.length]);

  const decide = useCallback((action) => {
    if (!item || decided[item.id]) { move(1); return; }
    setDecided((d) => ({ ...d, [item.id]: action }));
    if (action !== "skip") {
      editorialDecide(token, [item.id], action, null, userToken)
        .then(() => onDecided && onDecided(item.id, action))
        .catch((e) => {
          toast.error(e?.response?.data?.detail || `${action} failed`);
          setDecided((d) => { const n = { ...d }; delete n[item.id]; return n; });
        });
    }
    if (idx < items.length - 1) move(1);
  }, [item, decided, idx, items.length, token, userToken, move, onDecided]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "escape") { e.preventDefault(); details ? setDetails(false) : onClose(); }
      else if (k === "a") { e.preventDefault(); decide("approve"); }
      else if (k === "r") { e.preventDefault(); decide("reject"); }
      else if (k === "s") { e.preventDefault(); decide("skip"); }
      else if (k === "arrowright") { e.preventDefault(); move(1); }
      else if (k === "arrowleft") { e.preventDefault(); move(-1); }
      else if (k === " ") { e.preventDefault(); setExpanded((v) => !v); }
      else if (k === "enter") { e.preventDefault(); setDetails((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, move, onClose, details]);

  if (!item) return null;
  const e = item.entity || {};
  const wikiUrl = (item.source || "").split("— ").pop();
  const matchReason = item.notes || "Auto-ingested Wikimedia candidate";
  const verdict = decided[item.id];

  return (
    <div className="fixed inset-0 z-[90] bg-[#0B0A09]/98 backdrop-blur-sm flex flex-col"
         data-testid="rapid-triage" role="dialog" aria-modal="true">
      {/* Header — position + tally */}
      <div className="flex items-center gap-4 px-4 sm:px-6 h-12 border-b border-[#2D2A26] shrink-0">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          Rapid Triage
        </span>
        <span className="font-mono text-[10px] text-[#757065]" data-testid="triage-position">
          {idx + 1} / {items.length}
        </span>
        <div className="ml-auto flex items-center gap-3 font-mono text-[10px]">
          <span className="text-[#5DD4B3]" data-testid="triage-approved">✓ {tally.approve}</span>
          <span className="text-[#FFA9A0]">✕ {tally.reject}</span>
          <span className="text-[#757065]">→ {tally.skip}</span>
          <button onClick={onClose} data-testid="triage-close"
                  className="text-[#757065] hover:text-[#E4DFD3] ml-2"><X size={15} /></button>
        </div>
      </div>

      {/* Body — candidate vs record */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-y-auto">
        <div className={`flex items-center justify-center bg-black p-4 transition-all ${expanded ? "lg:w-2/3" : "lg:w-1/2"}`}>
          <AuthImg key={item.id} token={token} userToken={userToken} assetId={item.id}
                   kind={expanded ? "webp" : "thumb_webp"} alt={item.caption || e.name}
                   className={`w-auto object-contain ${expanded ? "max-h-[78vh]" : "max-h-[52vh]"}`}
                   style={{ filter: "sepia(0.1)" }} />
        </div>
        <div className="flex-1 p-4 sm:p-6 border-t lg:border-t-0 lg:border-l border-[#2D2A26]">
          <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065]">
            {e.kind || "record"} · {item.role || "attachment"}
          </div>
          <h2 className="font-heading text-2xl text-[#E4DFD3] mt-1 leading-tight">
            {e.name}
          </h2>
          <div className="mt-1 font-mono text-[9px] text-[#757065]">
            {e.cameo_count || 0} dossier appearance{(e.cameo_count || 0) !== 1 ? "s" : ""}
          </div>
          {item.caption && (
            <p className="text-[12.5px] text-[#A39E93] leading-relaxed mt-3 max-w-lg">{item.caption}</p>
          )}
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 max-w-md">
            <div>
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C]">Confidence</div>
              <div className="font-display text-3xl leading-none mt-0.5"
                   style={{ color: confColor(item.confidence) }}
                   data-testid="triage-confidence">{item.confidence}</div>
            </div>
            <div>
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C]">License</div>
              <div className="mt-1 inline-flex items-center gap-1 border border-[#3FB950]/40 text-[#5DD4B3] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em]">
                <ShieldCheck size={9} /> {item.rights || "unknown"}
              </div>
            </div>
          </div>
          {(item.flags?.target_already_published || item.flags?.hash_duplicates > 0) && (
            <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.15em] text-[#E0A526]">
              ⚠ {item.flags.target_already_published ? "entity already has published media" : ""}
              {item.flags.hash_duplicates > 0 ? ` · ${item.flags.hash_duplicates} identical file(s)` : ""}
            </div>
          )}
          {details && (
            <div className="mt-4 border border-[#2D2A26] p-3 space-y-1.5 max-w-lg"
                 data-testid="triage-details">
              <div className="font-mono text-[9px] text-[#757065]">Match reason: <span className="text-[#A39E93]">{matchReason}</span></div>
              {item.credit && <div className="font-mono text-[9px] text-[#757065]">Credit: <span className="text-[#A39E93]">{item.credit}</span></div>}
              {wikiUrl.startsWith("http") && (
                <a href={wikiUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 font-mono text-[9px] text-[#9FA8D4] hover:underline">
                  source article <ExternalLink size={9} />
                </a>
              )}
              {e.url && (
                <a href={e.url} target="_blank" rel="noreferrer"
                   className="block font-mono text-[9px] text-[#A68F5E] hover:underline">
                  open entity record →
                </a>
              )}
            </div>
          )}
          {verdict && (
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em]"
                 style={{ color: verdict === "approve" ? "#5DD4B3" : verdict === "reject" ? "#FFA9A0" : "#757065" }}
                 data-testid="triage-verdict">
              {verdict === "approve" ? "✓ Published" : verdict === "reject" ? "✕ Rejected" : "Skipped"}
            </div>
          )}
          {/* Touch fallback buttons */}
          <div className="mt-6 flex gap-2 lg:hidden">
            <button onClick={() => decide("approve")} className="flex-1 bg-[#1d3b2a] border border-[#3FB950]/50 text-[#5DD4B3] py-2.5 font-mono text-[10px] uppercase tracking-[0.2em]"><Check size={11} className="inline mr-1" />Approve</button>
            <button onClick={() => decide("reject")} className="flex-1 border border-[#D92D20]/50 text-[#FFA9A0] py-2.5 font-mono text-[10px] uppercase tracking-[0.2em]"><X size={11} className="inline mr-1" />Reject</button>
            <button onClick={() => decide("skip")} className="border border-[#3A3630] text-[#757065] px-4 font-mono text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-1" data-testid="triage-skip-touch"><SkipForward size={11} /> Skip</button>
          </div>
        </div>
      </div>

      {/* Footer — keymap */}
      <div className="hidden lg:flex items-center gap-5 px-6 h-11 border-t border-[#2D2A26] shrink-0">
        <Key k="A" label="approve" /><Key k="R" label="reject" /><Key k="S" label="skip" />
        <Key k="←→" label="move" /><Key k="Space" label="expand" />
        <Key k="Enter" label="details" /><Key k="Esc" label="exit" />
      </div>
    </div>
  );
};

export default RapidTriage;
